<template>
  <component :is="embedded ? 'div' : 'section'" :class="rootClass">
    <div :class="embedded ? null : 'dept-modern'">
      <div class="dept-contacts__grid dept-modern__card">
        <div class="dept-contacts__info">
          <h2 v-if="heading" class="dept-contacts__title">{{ heading }}</h2>
          <div class="dept-contacts__rows">
            <div
              v-for="(phone, idx) in phones"
              :key="'ph-' + idx"
              class="dept-contacts__row"
            >
              <div class="dept-contacts__row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                  <path d="M6.5 4.5h2l1.2 3.2a1 1 0 00.95.68l2.6.4a1 1 0 01.55 1.55l-1.6 1.9a12.5 12.5 0 005.7 5.7l1.9-1.6a1 1 0 011.55.55l.4 2.6a1 1 0 00.68.95L19.5 18h2a1.5 1.5 0 001.5-1.5C21 10.5 13.5 3 6.5 3A1.5 1.5 0 005 4.5v2z" stroke="currentColor" stroke-width="1.4"/>
                </svg>
              </div>
              <div class="dept-contacts__row-text">
                <span class="dept-contacts__label">{{ phone.label || 'Телефон' }}</span>
                <a class="dept-contacts__value dept-contacts__value--link" :href="'tel:' + phoneHref(phone.value)">{{ phone.value }}</a>
              </div>
            </div>
            <div v-if="email" class="dept-contacts__row">
              <div class="dept-contacts__row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                  <rect x="3.5" y="5.5" width="17" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/>
                  <path d="M4 7l8 6 8-6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="dept-contacts__row-text">
                <span class="dept-contacts__label">Электронная почта</span>
                <a class="dept-contacts__value dept-contacts__value--link" :href="'mailto:' + email">{{ email }}</a>
              </div>
            </div>
            <div v-if="address" class="dept-contacts__row">
              <div class="dept-contacts__row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                  <path d="M12 21s6-5.2 6-10.5a6 6 0 10-12 0C6 15.8 12 21 12 21z" stroke="currentColor" stroke-width="1.4"/>
                  <circle cx="12" cy="10.5" r="2" stroke="currentColor" stroke-width="1.4"/>
                </svg>
              </div>
              <div class="dept-contacts__row-text">
                <span class="dept-contacts__label">Адрес</span>
                <span class="dept-contacts__value">{{ address }}</span>
              </div>
            </div>
          </div>
        </div>
        <div ref="mapCol" class="dept-contacts__map hide-in-accessibility">
          <YandexMap
            :key="'contacts-map-' + mapHeight"
            class="dept-contacts__ymap"
            fill
            :height="mapHeight"
            :scroll-zoom="false"
          />
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import YandexMap from '@/components/Map/MapYandex.vue';

export default {
  name: 'DepartmentContacts',
  components: { YandexMap },
  props: {
    heading: { type: String, default: '' },
    phones: { type: Array, default: () => [] },
    email: { type: String, default: '' },
    address: { type: String, default: '' },
    mapImage: { type: String, default: '' },
    mapAlt: { type: String, default: '' },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      mapHeight: 360,
      mapRo: null,
    };
  },
  computed: {
    rootClass() {
      return this.embedded
        ? 'dept-contacts dept-contacts--embedded'
        : 'dept-contacts dept-modern__section';
    },
  },
  mounted() {
    this.$nextTick(() => this.bindMapHeight());
  },
  beforeDestroy() {
    if (this.mapRo) {
      this.mapRo.disconnect();
      this.mapRo = null;
    }
  },
  methods: {
    phoneHref(value) {
      return String(value || '').replace(/[^\d+]/g, '');
    },
    bindMapHeight() {
      const el = this.$refs.mapCol;
      if (!el) return;
      const apply = () => {
        const h = Math.round(el.clientHeight || 0);
        if (h >= 200 && Math.abs(h - this.mapHeight) > 16) {
          this.mapHeight = h;
        }
      };
      apply();
      if (typeof ResizeObserver === 'undefined') return;
      this.mapRo = new ResizeObserver(() => apply());
      this.mapRo.observe(el);
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;

.dept-contacts--embedded {
  height: 100%;
}

.dept-contacts__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 0;
  overflow: hidden;
  height: 100%;
  min-height: 280px;
  box-shadow: 0 2px 12px rgba(102, 204, 102, 0.05);

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
}

.dept-contacts__info {
  padding: 28px 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 767.98px) {
    padding: 24px 20px;
  }
}

.dept-contacts__title {
  margin: 0 0 22px;
  font-family: "TTDrugs", sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: $green-text;
}

.dept-contacts__rows {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.dept-contacts__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dept-contacts__row-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eaf7ea;
  color: $green;
}

.dept-contacts__row-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding-top: 1px;
}

.dept-contacts__label {
  display: block;
  font-family: "Circe", sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
}

.dept-contacts__value {
  display: block;
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  color: #2d3436;
}

.dept-contacts__value--link {
  color: $green-text;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.dept-contacts__map {
  position: relative;
  min-height: 320px;
  align-self: stretch;
  background: #eaf7ea;
  overflow: hidden;

  @media (max-width: 767.98px) {
    min-height: 280px;
  }
}

.dept-contacts__ymap {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}
</style>
